/**
 * The ways into a new Brand Kit -- from a logo, from designs, from a website or blank -- and the
 * "Set up brand kit" prompt that leads to them.
 */

import type {UserMediaModalProps} from '@Modals/user-media-modal';
import {openUserMediaModal} from '@Modals/user-media-modal';
import {UploadsMediaSubTab, UploadsMediaType} from '@Panels/user-media-panel/user-media-panel.types';
import type {AddMediaItem} from '@Libraries/add-media-library';
import {MultiMediaUploadSource} from '@Libraries/user-media-library';
import {BRAND_LOGO_ACCEPTED_MIMETYPE} from '@Panels/user-media-panel/components/no-brand-logos/no-brand-logos.types';
import {isMyStuffPage} from '@Components/nav-search/nav-search.helper';
import {closeAllModals} from '@Components/modal-container/modal-container-reducer';
import {
  BRAND_CREATION_SOURCES,
  buildAjaxOptsForNewBrand,
  canUserAccessBrandKits,
  getActiveBrandId,
  getBrandsStateFromStore,
  getDefaultBrandKitName,
  getFirstBrandKitTemplate,
  doesUserNeedUpsellingForBrandKits,
  openAppropriateUpsellingDialogForBrandKits,
} from '@Libraries/brands-library';
import {addUserImageToBrand, createBrandFromWebsite, createBrandWithActiveContext, fetchBrandAssets} from '@Components/mystuff-brands/brands-thunk';
import {onAjaxError} from '@Utils/ajax.util';
import {noop} from '@Utils/general.util';
import {BrandKitCreationRoute} from '@Components/mystuff-brands/components/brand-kit-creation-empty-state/brand-kit-creation-empty-state.types';
import {BrandAssetType, BrandKitExtractionSource, BrandKitSource} from '@Components/mystuff-brands/brand-kits.types';
import type {DesignSelection} from '@Components/social-media/social-media.types';
import {seedBrandKitColorsFromFirstTemplate, seedBrandKitFromPickedLogo} from '@Libraries/brand-kit-logo-extraction.library';
import {finishBrandKitExtraction, showBrandKitExtractionFeedbackToast, startBrandKitExtraction} from '@Libraries/brand-kit-extraction-feedback.library';
import {
  addTemplatesToBrandKit,
  dispatchForEachAndReportFailure,
  getSelectedPosterHashedIds,
  isStoredImageItem,
  openDesignsPicker,
  resetUploadsPanelTabs,
  showBrandKitTemplatesAddFailedGrowl,
  showBrandKitUploadsFailedGrowl,
} from '@Libraries/brand-kit-asset-adds.library';
import {NewBrandKitGA4Source} from '@Libraries/ga-events';
import {trackNewBrandKit} from '@Libraries/brand-kit-ga4.library';

/** The id the designs picker gets before the kit it fills exists: no kit's templates are left out of it. */
const NO_BRAND_KIT_YET_ID = '';

/** "Start with your logo" builds the kit around one logo; more join it from the Logos card afterwards. */
const NEW_BRAND_KIT_LOGO_COUNT = 1;

/** The page a "Set up brand kit" prompt leads to: My Stuff's Brand Kits, where every way into a kit starts. */
const BRAND_KITS_PAGE_PATH = 'posters/mine#/brands';

/** What building a kit from a website needs from the URL step that asked for it. */
interface BrandKitFromWebsiteOptions {
  websiteUrl: string;
  /** One of BRAND_CREATION_SOURCES.*, for the GA4 event the kit's creation reports. */
  creationSource: string;
  /** Closes the URL popover or sheet; resolves once it is off screen. */
  closeWebsiteUrlStep: () => Promise<void>;
}

/**
 * The four ways into a new kit. The logo and design routes ask for their pick first and create the kit
 * only once there is one, so backing out of the picker leaves nothing behind; a blank kit is created at once.
 */
export const startBrandKitCreationForRoute = async (route: BrandKitCreationRoute, creationSource: string = BRAND_CREATION_SOURCES.EMPTY_STATE): Promise<void> => {
  // A website kit is built from the site in one request by the URL popover, never as a blank kit first.
  if (route === BrandKitCreationRoute.WEBSITE) {
    return;
  }

  // Every route ends in a new kit, which the server refuses once the plan's kits are used up.
  if (doesUserNeedUpsellingForBrandKits()) {
    openAppropriateUpsellingDialogForBrandKits();
    return;
  }

  if (route === BrandKitCreationRoute.LOGO) {
    openLogoPickerForNewBrandKit(creationSource);
    return;
  }

  // The handoff titles the phone and tablet sheet of this route "My designs": the picks start a kit, they are not templates yet.
  if (route === BrandKitCreationRoute.DESIGN) {
    openDesignsPicker({
      brandId: NO_BRAND_KIT_YET_ID,
      bottomSheetTitle: window.i18next.t('pmwjs_my_designs'),
      onAddSelected: (selectedDesigns: DesignSelection[]): void => {
        void startBrandKitFromDesigns(creationSource, selectedDesigns);
      },
    });
    return;
  }

  await createBrandKitForRoute(creationSource, NewBrandKitGA4Source.START_FROM_SCRATCH);
};

/**
 * Asks for the designs before anything exists, so a closed picker leaves no kit behind. Picked designs join the new kit as Brand Templates
 * and its colours are read off the first one, behind the page's loading skeleton.
 */
const startBrandKitFromDesigns = async (creationSource: string, selectedDesigns: DesignSelection[]): Promise<void> => {
  startBrandKitExtraction();

  try {
    const newBrandId = await createBrandKitForRoute(creationSource, NewBrandKitGA4Source.EXTRACT_FROM_DESIGN);

    if (newBrandId) {
      await addDesignsAndSeedBrandKit(newBrandId, selectedDesigns);
    }
  } finally {
    finishBrandKitExtraction();
  }
};

/** The "Do they look right?" toast asks about what was pulled, so it shows only once colours or fonts have landed. */
const addDesignsAndSeedBrandKit = async (brandId: string, selectedDesigns: DesignSelection[]): Promise<void> => {
  const refusedCount = await addTemplatesToBrandKit(brandId, getSelectedPosterHashedIds(selectedDesigns));

  if (refusedCount > 0) {
    showBrandKitTemplatesAddFailedGrowl();
  }

  const [haveColorsLanded, haveFontsLanded] = await Promise.all([seedBrandKitColorsFromFirstTemplate(brandId), seedBrandKitFontsFromFirstTemplate(brandId)]);

  if (haveColorsLanded || haveFontsLanded) {
    showBrandKitExtractionFeedbackToast(BrandKitExtractionSource.DESIGN);
  }
};

/**
 * The server reads the fonts off the design's text and fills the kit's Heading, Subheading and Body as far as they
 * go, from the same first template the colours come from. Resolves to whether any landed; a failure leaves the slots empty.
 */
const seedBrandKitFontsFromFirstTemplate = async (brandId: string): Promise<boolean> => {
  const posterHashedId = getFirstBrandKitTemplate(brandId)?.posterHashedId;

  if (!posterHashedId) {
    return false;
  }

  try {
    await window.PMW.writeLocal('brand/addBrandFontsFromDesign', {brandId, posterHashedId});
    await fetchBrandAssets(brandId, BrandAssetType.BRAND_FONTS);
  } catch {
    return false;
  }

  return (getBrandsStateFromStore().brandContent[brandId]?.fonts.ids.length ?? 0) > 0;
};

/**
 * Resolves to the new kit, or null when creating it failed: a failure leaves the previous kit active.
 * A kit that was made is counted under its route, whether or not what the route then pulls into it lands.
 */
const createBrandKitForRoute = async (creationSource: string, ga4Source: NewBrandKitGA4Source): Promise<string | null> => {
  const previouslyActiveBrandId = getActiveBrandId();
  await createBrandWithActiveContext(buildAjaxOptsForNewBrand(getDefaultBrandKitName(getBrandsStateFromStore().brands)), creationSource);
  const newBrandId = getActiveBrandId();

  if (newBrandId === previouslyActiveBrandId) {
    return null;
  }

  trackNewBrandKit(ga4Source);
  return newBrandId;
};

/**
 * Asks for one logo before anything exists, so a closed picker leaves no kit behind and a batch cannot start several. Uploads use the plain source:
 * the brand-logo source would file them into the kit active now, not the one about to be made.
 */
const openLogoPickerForNewBrandKit = (creationSource: string): void => {
  openBrandKitUploadsPicker({
    ...getBrandKitPickerPresentation(),
    uploadAcceptedMimetype: BRAND_LOGO_ACCEPTED_MIMETYPE,
    uploadSource: MultiMediaUploadSource.UPLOAD,
    maxSelectionNumber: NEW_BRAND_KIT_LOGO_COUNT,
    defaultTab: UploadsMediaType.IMAGE,
    defaultActiveSubTab: UploadsMediaSubTab.LOGO,
    mediaTypesToShow: [UploadsMediaType.IMAGE, UploadsMediaType.BRAND_KIT],
    onAddSelected: (items: AddMediaItem[]): void => {
      void startBrandKitFromPickedLogo(creationSource, items);
    },
  });
};

/** The uploads dialog stays mounted between openings, so its own reset on mount never runs again. */
const openBrandKitUploadsPicker = (pickerProps: UserMediaModalProps): void => {
  resetUploadsPanelTabs(pickerProps.defaultTab, pickerProps.defaultActiveSubTab);
  openUserMediaModal(pickerProps);
};

/**
 * The handoff's picker chrome: the dialog is titled My Uploads, and its AI pills read AI generated where
 * the editor's read AI Generations. Only the kit's pickers wear it.
 */
const getBrandKitPickerPresentation = (): Pick<UserMediaModalProps, 'title' | 'subTabLabels' | 'hideFooterSecondaryActions' | 'isBrandKitPicker'> => {
  const aiGeneratedLabel = window.i18next.t('pmwjs_ai_generated');

  return {
    title: window.i18next.t('pmwjs_my_uploads'),
    subTabLabels: {
      [UploadsMediaSubTab.AI_IMAGE]: aiGeneratedLabel,
      [UploadsMediaSubTab.AI_VIDEO]: aiGeneratedLabel,
    },
    hideFooterSecondaryActions: true,
    isBrandKitPicker: true,
  };
};

/**
 * Once a logo is picked, or has finished uploading, the page shows its loading skeleton while the kit is
 * created, the logo joins it and its colours are seeded from the logo.
 */
const startBrandKitFromPickedLogo = async (creationSource: string, items: AddMediaItem[]): Promise<void> => {
  const pickedLogo = items.find(isStoredImageItem);

  if (pickedLogo === undefined) {
    return;
  }

  startBrandKitExtraction();

  try {
    const newBrandId = await createBrandKitForRoute(creationSource, NewBrandKitGA4Source.EXTRACT_FROM_LOGO);

    if (newBrandId) {
      await addLogoAndSeedBrandKit(newBrandId, pickedLogo.hashedFilename);
    }
  } finally {
    finishBrandKitExtraction();
  }
};

/** A logo that fails to join leaves the new kit empty, as a failed pick in the kit would. */
const addLogoAndSeedBrandKit = async (brandId: string, logoHashedFilename: string): Promise<void> => {
  const hasFailedToAdd = await dispatchForEachAndReportFailure([logoHashedFilename], (hashedFilename) => {
    return addUserImageToBrand({brandId, hashedFilename, brandAssetType: BrandAssetType.BRAND_LOGOS});
  });

  if (hasFailedToAdd) {
    showBrandKitUploadsFailedGrowl();
    return;
  }

  await fetchBrandAssets(brandId, BrandAssetType.BRAND_LOGOS);
  await seedBrandKitFromPickedLogo(brandId);
};

/**
 * Builds a kit from a website in one request; the reducer makes it the active kit. The URL step closes before a failure's error dialog opens,
 * so the dialog lands on top of the page rather than over the closing field.
 */
export const createBrandKitFromWebsite = async ({websiteUrl, creationSource, closeWebsiteUrlStep}: BrandKitFromWebsiteOptions): Promise<void> => {
  let hasCreatedKit = false;
  let openCreationErrorDialog = noop;

  await createBrandFromWebsite(
    {...buildAjaxOptsForNewBrand(websiteUrl, null, BrandKitSource.WEBSITE), brandCreationSource: creationSource},
    (error: unknown, message?: string, logError?: boolean): void => {
      openCreationErrorDialog = (): void => {
        onAjaxError(error, message, logError);
      };
    },
    () => {
      hasCreatedKit = true;
    }
  );

  await closeWebsiteUrlStep();
  openCreationErrorDialog();

  if (hasCreatedKit) {
    trackNewBrandKit(NewBrandKitGA4Source.EXTRACT_FROM_WEBSITE);
    showBrandKitExtractionFeedbackToast(BrandKitExtractionSource.WEBSITE);
  }
};

/**
 * "Set up brand kit" from a surface with no kit: inside My Stuff it switches to Brand Kits and closes the asking modal; elsewhere (the editor)
 * it opens Brand Kits in a new tab so work in progress stays put. A user without Brand Kits gets the upsell.
 */
export const openBrandKitsSetup = (): void => {
  if (!canUserAccessBrandKits()) {
    openAppropriateUpsellingDialogForBrandKits({shouldOpenInNewTab: true});
    return;
  }

  const brandKitsUrl = window.PMW.util.site_url(BRAND_KITS_PAGE_PATH);

  if (!isMyStuffPage()) {
    window.open(brandKitsUrl, '_blank');
    return;
  }

  window.PMW.redux.store.dispatch(closeAllModals());
  window.location.href = brandKitsUrl;
};
